import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import Modal from "../Modal";
import { FormikPinInput, PinInputField } from "../Otp";
import {
  completeTwoFactors,
  toggleTwoFactorsModal,
  twoFactorsReset,
} from "@redux/twoFactors/actions";
import { initialValues, validationSchema } from "./form";
import { setToken } from "@/firebase";
import { isSWSupported } from "@helpers/sw";
import { notificationPermissions } from "@const/sw";

import styles from "./index.module.scss";
import Loader from "../Loader";
import handleRedirect from "../../helpers/handleRedirect";
import { REDIRECT_PATHS } from "@pages/AuthRedirects";

const TwoFactorsAuthModal = () => {
  const { t } = useTranslation("two-factors-auth");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues,
    validationSchema,
  });

  const { isLoading, isSuccess, error, isModalVisible } = useSelector(
    ({ twoFactorsReducer }) => twoFactorsReducer,
  );

  const { isPushNotificationsAllowed } = useSelector(
    ({ profileReducer }) => profileReducer,
  );

  useEffect(() => {
    if (!isSuccess) return;
    if (REDIRECT_PATHS.includes(window.location.pathname)) {
      handleRedirect(navigate);
    }
    if (!isSWSupported()) return;
    if (Notification.permission === notificationPermissions.denied) return;
    if (!isPushNotificationsAllowed) return;

    setToken();
  }, [isSuccess, isPushNotificationsAllowed]);

  useEffect(() => {
    if (!isSuccess) return;

    dispatch(twoFactorsReset());
  }, [isSuccess]);

  const closeModal = () => {
    dispatch(toggleTwoFactorsModal(false));
  };

  useEffect(() => {
    if (formik.values.token?.length !== 6 || isLoading) return;
    dispatch(completeTwoFactors({ token: formik.values.token }));
  }, [formik.values]);

  useEffect(() => {
    if (isModalVisible) return;

    dispatch(twoFactorsReset());
    formik.resetForm();
  }, [isModalVisible]);

  return (
    <Modal
      centered
      unmountOnClose
      showCancel={false}
      showConfirm={false}
      onClose={closeModal}
      isOpen={isModalVisible}
      footerClassName="d-none"
      bodyClassName={styles.body}
    >
      {isLoading && (
        <div
          className={classNames(
            "position-absolute d-flex justify-content-center align-items-center",
            styles.codeSpinner,
          )}
        >
          <Loader className="spinning" />
        </div>
      )}
      <div className={styles.content}>
        <div className="text-primary text-center h4 h3-xl mb-3">
          {t("TITLE")}
        </div>
        <div
          className={classNames(
            "text-primary text-center text-body-1 text-regular text-medium mx-auto",
            styles.subtitle,
          )}
        >
          {t("PLEASE_ENTER_2FA")}
        </div>
        <FormikProvider value={formik}>
          <Form
            className={classNames(
              "d-flex flex-column align-items-center",
              styles.form,
            )}
          >
            <FormikPinInput
              name="token"
              className="mb-3 position-relative"
              customError={error?.message}
            >
              <PinInputField customError={error?.message} />
              <PinInputField customError={error?.message} />
              <PinInputField customError={error?.message} />
              <PinInputField customError={error?.message} />
              <PinInputField customError={error?.message} />
              <PinInputField customError={error?.message} />
              {error?.message ? (
                <span className={classNames("caption", styles.error)}>
                  {error?.message}
                </span>
              ) : null}
            </FormikPinInput>
          </Form>
        </FormikProvider>
      </div>
    </Modal>
  );
};

export default TwoFactorsAuthModal;
